import CloseIcon from "@mui/icons-material/Close";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";
import {
  DialogContent,
  DialogTitle,
  Typography,
  TextField,
  styled,
  Dialog,
  Autocomplete,
  Stack,
  Box,
  useTheme,
  Button
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import {
  ProgressButton,
  formatDateDayjs,
  globalDateFormat,
  useToast,
  CheckmarkCircle24Px,
  LeafIcon,
  Error24Px,
  Warning24Px,
  SquareIconButton
} from "@qubit/autoparts";
import dayjs, { Dayjs } from "dayjs";
import { useCallback, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { ConnectedProps, connect } from "react-redux";

import { getUserClientId } from "~/api/usersTypes/auth0Profile";
import { useAppDispatch, useAppSelector } from "~/app/store";
import { createProductSearchOptions } from "~/config/searchProductConfig";

import { AutostoreBinWithAnimation } from "~/features/autostoreBin/AutostoreBinWithAnimation";
import { addSanitizedBin } from "~/features/autostorePutaway/autostorePutaway.slice";
import { AlertInfo } from "~/features/inventory/components/AlertInfo";
import { useViewType } from "~/features/inventory/useViewType";
import { useDebounce } from "~/hooks/useDebounce";
import { useFcNavigate } from "~/hooks/useFcNavigate";

import { useBarcodeScanner } from "~/lib/barCodeScan";
import { CompartmentOccupancy } from "~/lib/binCompartmentOccupancy";
import {
  checkIsExpiration,
  generateInventoryPath,
  getMinInventoryExpirationDate,
  inventoryDateLabel,
  isExpirationValid,
  searchProduct
} from "~/lib/helpers";
import { QUANTITY_MAX_VALUE } from "~/lib/shared";

import {
  getVariantByVariantId,
  clearSelectedVariant
} from "~/redux/actions/inventory";
import { StoreState } from "~/redux/reducers";
import { selectIsShortSideBin } from "~/redux/selectors/workstationsSelectors";
import { MeasuredValueDto } from "~/types/api";

type ProductResult = {
  type: "product" | "bin";
  variantId: Guid;
  displayText: string;
};

const Root = styled(Dialog)(() => ({
  padding: 0,
  margin: 0,
  fontWeight: 400,
  fontFamily: "Roboto"
}));

const mapStateToProps1 = (state: StoreState) => ({
  clientId: getUserClientId(state.login.profile),
  selectedVariant: state.inventory.selectedVariant
});

const connector = connect(mapStateToProps1, {
  getVariantByVariantId,
  clearSelectedVariant
});

type PropsFromRedux = ConnectedProps<typeof connector>;

type AutostorePutawayModalProps = PropsFromRedux & {
  open: boolean;
  onClose: () => void;
  maxWidth?: "xs" | "sm" | "md" | "lg";
  browserWidth: number;
  binId: number | null;
  onPutawayButtonClick: (() => Promise<void>) | (() => void);
  changeQuantityFunc: (quantity: number) => void;
  inventoryDate: Dayjs | null;
  onDateChange: (date: Dayjs | null) => void;
  lotNumber?: string | null;
  /** if provided, will display a field for entering lot number */
  onLotNumberChange?: (lotNumber: string | null) => void;
  numberOfRows?: number;
  numberOfColumns?: number;
  selectedCompartment?: number;
  compartmentOccupancy?: CompartmentOccupancy[];
  inv_inventoryDateLabel: string;
  inv_inventoryDateRequired: boolean;
  decantRate?: MeasuredValueDto;
  remaining?: MeasuredValueDto;
  isExpirationRequired?: boolean;
  showProductSearch?: boolean;
  binLoading?: boolean;
  setSelectedCompartment?: (compartmentNumber: number) => void;
  /** if true, will show Cancel button and disable close by clicking out of modal */
  showCancelButton?: boolean;
  portId?: number;
};

export function AutostorePutawayModalComponent(
  props: AutostorePutawayModalProps
) {
  const { t } = useTranslation();
  const theme = useTheme();

  const inductionStrategy = useAppSelector(
    (state) =>
      state.store.usersFulfillmentCenter?.clientConfiguration?.inductionStrategy
  );
  const sanitizedBins = useAppSelector(
    (state) => state.autostorePutaway.sanitizedBins
  );
  const isShortSideBin = useAppSelector(selectIsShortSideBin);
  const {
    open,
    showCancelButton = false,
    onClose,
    maxWidth,
    binId,
    onPutawayButtonClick,
    changeQuantityFunc,
    inventoryDate,
    numberOfRows,
    numberOfColumns,
    selectedCompartment,
    compartmentOccupancy,
    inv_inventoryDateLabel,
    onDateChange,
    lotNumber,
    onLotNumberChange,
    inv_inventoryDateRequired,
    decantRate,
    remaining,
    isExpirationRequired,
    clientId,
    showProductSearch,
    binLoading = false,
    setSelectedCompartment,
    clearSelectedVariant,
    selectedVariant,
    portId
  } = props;
  const isWholeBin = numberOfRows === 1 && numberOfColumns === 1;
  const requiresWholeBin =
    selectedVariant?.isSanitizeTargetBin || selectedVariant?.isFoodSafetyItem;
  const iswholeBinRequirementUnmet = requiresWholeBin && !isWholeBin;
  const { errorToast } = useToast();
  const dispatch = useAppDispatch();
  const { isHoldsPage, isProductView } = useViewType();
  const navigate = useFcNavigate();
  const quantityMaxValue = QUANTITY_MAX_VALUE;
  const [localQuantityValue, setLocalQuantityValue] = useState(0);
  const [wasClicked, setWasClicked] = useState(false);
  const [autocompleteOptions, updateAutocompleteOptions] = useState<
    ProductResult[]
  >([] as ProductResult[]);
  const [autocompleteValue, setAutocompleteValue] = useState("");

  useEffect(() => {
    if (!selectedVariant) {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
      setAutocompleteValue("");
    } else {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
      setAutocompleteValue(selectedVariant.productName);
    }

    // clear user selected or shelf life date when the selected variant changes
    onDateChange(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVariant]);

  // if selectedVariant has shelfLife and user hasn't set inventoryDate, set inventoryDate to today + shelfLife
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (selectedVariant?.shelfLife && !inventoryDate) {
      const newDate = dayjs().add(selectedVariant.shelfLife, "days");
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
      onDateChange(newDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryDate, selectedVariant]);

  const selectedOption =
    autocompleteOptions.find(
      (option) => option.variantId === selectedVariant?.variantId
    ) || null;

  const disableButtonForProductSearch =
    binLoading || (showProductSearch && !selectedVariant);

  const isLotNumberRequiredAndEmpty =
    onLotNumberChange && selectedVariant?.isLotNumberRequired && !lotNumber;

  const autocompleteSearchProducts = useCallback(
    async (input: string | null): Promise<void> => {
      if (!input) {
        clearSelectedVariant();
        return;
      }
      if (input.length) {
        try {
          const hits = clientId ? await searchProduct(input) : [];

          const hitsAsAutocompleteRecords = createProductSearchOptions({
            hits
          });
          updateAutocompleteOptions(hitsAsAutocompleteRecords);
        } catch {
          errorToast("Could not get product list");
        }
      }
    },
    [clearSelectedVariant, clientId, errorToast]
  );

  const searchValue = useDebounce(autocompleteValue, 300, autocompleteValue);
  useEffect(() => {
    if (searchValue?.length) void autocompleteSearchProducts(searchValue);
  }, [searchValue, autocompleteSearchProducts]);

  // if we dont pass in a remaining prop, set it to the max quantity
  const remainingValue = remaining?.value ?? quantityMaxValue;
  const decantRateValue = decantRate?.value || 1;

  const formattedExpDate =
    !dayjs.isDayjs(inventoryDate) && inventoryDate !== null
      ? dayjs(inventoryDate)
      : inventoryDate;

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-live-state-to-parent
    changeQuantityFunc(localQuantityValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localQuantityValue]);

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
    setWasClicked(false);

    const initialQtyValue =
      !inductionStrategy?.includes("ContentCodes") &&
      decantRateValue < remainingValue
        ? decantRateValue
        : remaining?.value || 1;
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
    setLocalQuantityValue(initialQtyValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decantRateValue, remainingValue, binId]);
  // anytime a user changes the form values, reset the wasClicked state to ensure putaway button is enabled
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler, react-you-might-not-need-an-effect/no-chain-state-updates, react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
    if (wasClicked) setWasClicked(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVariant, localQuantityValue, inventoryDate, lotNumber]);

  useBarcodeScanner({
    disabled: !open,
    findScanMatch: async (buffer: string) => {
      const hits = clientId ? await searchProduct(buffer) : [];

      const hitsAsAutocompleteRecords = createProductSearchOptions({
        hits
      });

      if (hits.length > 0) {
        updateAutocompleteOptions(hitsAsAutocompleteRecords);
      }
      return hitsAsAutocompleteRecords?.[0] ?? null;
    },
    processScanMatch: (product) => {
      if (product) {
        void props.getVariantByVariantId(product.variantId);
      }
    }
  });

  const needsSanitization = !!(
    binId &&
    selectedVariant?.isSanitizeTargetBin &&
    !sanitizedBins.includes(binId)
  );

  const renderSanitizeButton = () => {
    if (!binId || sanitizedBins.includes(binId)) {
      return null;
    }
    return (
      <Button
        variant="contained"
        onClick={() => dispatch(addSanitizedBin(binId))}
        sx={{
          position: "absolute",
          mb: 1,
          width: "200px",
          maxWidth: "100%",
          alignSelf: "flex-end",
          justifySelf: "center"
        }}
      >
        {t("sanitize")}
      </Button>
    );
  };

  const renderSanitizedfIndicator = () => {
    if (
      binId &&
      sanitizedBins.includes(binId) &&
      selectedVariant?.isSanitizeTargetBin
    ) {
      return (
        <Stack
          direction="row"
          mt={1}
          spacing={1}
          justifyContent="center"
          height="74px"
        >
          <CheckmarkCircle24Px fill={theme.palette.success.dark} />
          <Typography>{t("sanitized")}</Typography>
        </Stack>
      );
    } else if (binId && iswholeBinRequirementUnmet) {
      return (
        <Box sx={{ maxWidth: "560px" }}>
          <AlertInfo
            message={t("this item requires a whole bin")}
            type="error"
            icon={<Error24Px style={{ fill: theme.palette.error.main }} />}
          />
        </Box>
      );
    } else if (binId && selectedVariant?.isSanitizeTargetBin && isWholeBin) {
      return (
        <Box sx={{ maxWidth: "560px" }}>
          <AlertInfo
            message={t("this item requires a sanitized bin")}
            type="sanitize"
            icon={<LeafIcon sx={{ color: theme.palette.infoPurple.dark }} />}
          />
        </Box>
      );
    } else if (binId && selectedVariant?.isFoodSafetyItem && isWholeBin) {
      return (
        <Box sx={{ maxWidth: "560px" }}>
          <AlertInfo
            message={t("this is a food safety item")}
            type="warning"
            icon={<Warning24Px fill={theme.palette.warning.dark} />}
          />
        </Box>
      );
    }
    return <Box height={82} />;
  };

  const handlePutawayClick = async () => {
    if (wasClicked) return;
    setWasClicked(true);

    if (remaining && localQuantityValue > remainingValue) {
      errorToast(t("max quantity for selected item is") + ` ${remainingValue}`);
    } else if (localQuantityValue === 0) {
      setLocalQuantityValue(0);
      errorToast(`Quantity must be greater than zero`);
    } else {
      setLocalQuantityValue(localQuantityValue);
      await onPutawayButtonClick();
    }
  };

  /** if dateConfig is expiration, check to make sure expiration date is in the future */
  const isPastDate =
    checkIsExpiration(inv_inventoryDateLabel) &&
    formattedExpDate?.isBefore(dayjs().startOf("day").add(1, "day"));

  const isDateInvalid =
    !isExpirationValid(
      inv_inventoryDateRequired ||
        isExpirationRequired ||
        selectedVariant?.isExpirationRequired,
      formattedExpDate
    ) || isPastDate;

  const inventoryDatePicker = (
    <DesktopDatePicker
      slotProps={{
        textField: {
          InputLabelProps: {
            shrink: true
          },
          sx: {
            ".MuiInputBase-input": {
              fontSize: "2rem"
            }
          },
          label: t(inventoryDateLabel(inv_inventoryDateLabel)),
          variant: "outlined",
          fullWidth: true,
          // this error becomes misleading if whole bin requirement is not met
          error: iswholeBinRequirementUnmet ? false : isDateInvalid
        }
      }}
      label={t(inventoryDateLabel(inv_inventoryDateLabel))}
      value={inventoryDate ? dayjs(formatDateDayjs(inventoryDate)) : null}
      format={globalDateFormat}
      onChange={onDateChange}
      minDate={getMinInventoryExpirationDate(inv_inventoryDateLabel)}
    />
  );

  return (
    <Root
      fullWidth
      maxWidth={maxWidth}
      open={open}
      onClose={
        showCancelButton
          ? undefined
          : () => {
              onClose();
              setAutocompleteValue("");
              setLocalQuantityValue(0);
              if (onLotNumberChange) onLotNumberChange(null);
            }
      }
    >
      <DialogTitle>
        <Typography
          sx={{
            fontSize: "24px"
          }}
        >
          {t("add inventory")}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack p={3} gap={3}>
          <Stack flexDirection="row" justifyContent="center" gap={3}>
            <Stack alignItems="center" flexGrow={1} gap={1}>
              <Typography variant="h5">
                {t("bin")} {binId || undefined}
              </Typography>
              <Box height="300px" display="flex">
                <AutostoreBinWithAnimation
                  portId={portId || 0}
                  pickQuantity={localQuantityValue}
                  pickCompartment={selectedCompartment || 0}
                  numberOfRows={numberOfRows || 1}
                  numberOfColumns={numberOfColumns || 1}
                  binId={binId || undefined}
                  hideBinId
                  compartmentOccupancy={compartmentOccupancy}
                  setSelectedCompartmentCallback={
                    setSelectedCompartment
                      ? (num) => setSelectedCompartment(num)
                      : undefined
                  }
                  renderBinButton={
                    selectedVariant?.isSanitizeTargetBin && isWholeBin
                      ? renderSanitizeButton()
                      : null
                  }
                  isShortSideBin={isShortSideBin}
                />
              </Box>
              {renderSanitizedfIndicator()}
            </Stack>
            <Stack gap={3} flexBasis={600}>
              {showProductSearch && (
                <Autocomplete<ProductResult>
                  options={autocompleteOptions}
                  filterOptions={(ops) => ops}
                  getOptionLabel={(option) => option.displayText}
                  onChange={(_e, option) => {
                    if (!option) {
                      props.clearSelectedVariant();
                      return;
                    }
                    if (isProductView) {
                      navigate({
                        pathname: generateInventoryPath(
                          "product",
                          isHoldsPage,
                          option?.variantId ?? ""
                        )
                      });
                    } else {
                      void props.getVariantByVariantId(option?.variantId);
                    }
                    setWasClicked(false);
                  }}
                  value={selectedOption}
                  onInputChange={(_e, inputValue) => {
                    setAutocompleteValue(inputValue);
                  }}
                  inputValue={autocompleteValue}
                  clearOnBlur={false}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      type="text"
                      label={t("search placeholder")}
                      error={!selectedVariant || iswholeBinRequirementUnmet}
                    />
                  )}
                />
              )}
              <Stack direction="row" gap={2} justifyContent="center">
                <TextField
                  label={t("quantity")}
                  variant="outlined"
                  size="small"
                  inputProps={{
                    sx: {
                      py: 3,
                      fontSize: "5em",
                      textAlign: "center"
                    }
                  }}
                  InputLabelProps={{
                    sx: {
                      color: "darkGray.main",
                      fontSize: 22
                    }
                  }}
                  value={localQuantityValue}
                  onChange={(e): void => {
                    const inputValue = parseInt(e.target.value || "0", 10);
                    if (
                      inputValue <= remainingValue &&
                      inputValue <= quantityMaxValue
                    ) {
                      setLocalQuantityValue(inputValue);
                    } else if (inputValue > remainingValue) {
                      errorToast(
                        t("max quantity for selected item is") +
                          ` ${remainingValue}`
                      );
                    }
                  }}
                />
                <Stack direction="column" gap={2} justifyContent="center">
                  <SquareIconButton
                    aria-label="increase quantity"
                    onClick={() => {
                      const newQty = localQuantityValue + 1;
                      if (
                        newQty <= remainingValue &&
                        newQty <= quantityMaxValue
                      ) {
                        setLocalQuantityValue(newQty);
                      } else if (remaining) {
                        errorToast(
                          t("max quantity for selected item is") +
                            ` ${remainingValue}`
                        );
                      }
                    }}
                    size="large"
                    disabled={
                      localQuantityValue >= remainingValue ||
                      localQuantityValue >= quantityMaxValue
                    }
                  >
                    <ExpandLessIcon sx={{ fontSize: 27 }} />
                  </SquareIconButton>

                  <SquareIconButton
                    aria-label="decrease quantity"
                    onClick={() => {
                      if (localQuantityValue - 1 > 0) {
                        setLocalQuantityValue(localQuantityValue - 1);
                      } else {
                        errorToast(`Quantity must be greater than zero`);
                      }
                    }}
                    size="large"
                  >
                    <ExpandMoreIcon sx={{ fontSize: 27 }} />
                  </SquareIconButton>
                </Stack>
              </Stack>
              <Stack direction="row" spacing={2}>
                {onLotNumberChange && (
                  <TextField
                    variant="outlined"
                    type="text"
                    sx={{ flex: "1 1 0" }}
                    slotProps={{ input: { style: { fontSize: "2rem" } } }}
                    label={t("lot number")}
                    value={lotNumber || ""}
                    onChange={(e) => onLotNumberChange(e.target.value)}
                    error={
                      // this error becomes misleading if whole bin requirement is not met
                      iswholeBinRequirementUnmet
                        ? false
                        : isLotNumberRequiredAndEmpty
                    }
                  />
                )}
                <Box flex={"1 1 0"}>{inventoryDatePicker}</Box>
              </Stack>
            </Stack>
          </Stack>
          <Stack flexDirection="row" gap={3}>
            {showCancelButton && (
              <ProgressButton
                buttonSize="xLarge"
                emphasis="high"
                responsive
                variant="contained"
                color="secondary"
                fullWidth
                style={{ flexGrow: 1 }}
                startIcon={<CloseIcon style={{ fontSize: 22 }} />}
                onClick={() => {
                  onClose();
                }}
              >
                {t("cancel")}
              </ProgressButton>
            )}
            <ProgressButton
              buttonSize="xLarge"
              emphasis="high"
              responsive
              variant="contained"
              color="primary"
              fullWidth
              style={{ flexGrow: 2 }}
              startIcon={<SystemUpdateAltIcon style={{ fontSize: 22 }} />}
              onClick={handlePutawayClick}
              disabled={
                !localQuantityValue ||
                wasClicked ||
                isDateInvalid ||
                disableButtonForProductSearch ||
                isLotNumberRequiredAndEmpty ||
                needsSanitization ||
                iswholeBinRequirementUnmet
              }
            >
              {t("put away")}
            </ProgressButton>
          </Stack>
        </Stack>
      </DialogContent>
    </Root>
  );
}

export const AutostorePutawayModal = connector(AutostorePutawayModalComponent);
